import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Mic, Upload, RotateCcw, Loader2, Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { createPost, createPostChunked, getResumableUpload } from '../../api/posts';
import { useCategories } from '../../api/categories';
import AudioRecorder from './AudioRecorder';
import MonetizationPicker, { MIN_TEASER } from './MonetizationPicker';

const MAX_AUDIO_BYTES = 80 * 1024 * 1024;
const CHUNKED_THRESHOLD = 20 * 1024 * 1024;

const extFromMime = (m = '') => {
  if (m.includes('mp4')) return '.m4a';
  if (m.includes('ogg')) return '.ogg';
  if (m.includes('mpeg')) return '.mp3';
  if (m.includes('wav')) return '.wav';
  return '.webm';
};

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const probeDuration = (url) => new Promise((resolve) => {
  const a = document.createElement('audio');
  a.preload = 'metadata';
  a.onloadedmetadata = () => resolve(Number.isFinite(a.duration) ? Math.round(a.duration) : 0);
  a.onerror = () => resolve(0);
  a.src = url;
});

/**
 * RaccontoAudioForm — Phase F6. Titolo + descrizione + audio da microfono
 * (Registra ora) o da file (Carica audio). Never auto-publishes: the user
 * always previews before Pubblica. Reuses the existing post upload pipeline.
 */
const RaccontoAudioForm = ({ onBack }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { categories } = useCategories();

  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [source, setSource] = useState(null); // null | 'record' | 'upload'
  const [audio, setAudio] = useState(null);   // { blob?, file?, url, mime, duration, fromRecording }
  const [recording, setRecording] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState('100');
  const [teaser, setTeaser] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resumable, setResumable] = useState(null); // {upload_id, percent, …} for THIS file

  const dirty = recording || (!!audio && !uploading);

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  useEffect(() => () => { if (audio?.url) URL.revokeObjectURL(audio.url); }, [audio]);

  const guardedBack = () => {
    if (dirty && !window.confirm('Vuoi uscire? La registrazione non pubblicata andrà persa.')) return;
    onBack();
  };

  const onRecorded = (blob, mime, duration) => {
    if (audio?.url) URL.revokeObjectURL(audio.url);
    setAudio({ blob, url: URL.createObjectURL(blob), mime, duration, fromRecording: true });
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!(f.type || '').startsWith('audio/')) {
      toast({ title: 'File non valido', description: 'Seleziona un file audio (mp3, m4a, ogg, wav…).', variant: 'destructive' });
      return;
    }
    if (f.size > MAX_AUDIO_BYTES) {
      toast({ title: 'File troppo grande', description: 'Il file audio può pesare al massimo 80 MB.', variant: 'destructive' });
      return;
    }
    if (audio?.url) URL.revokeObjectURL(audio.url);
    const url = URL.createObjectURL(f);
    const duration = await probeDuration(url);
    setAudio({ file: f, url, mime: f.type, duration, fromRecording: false });
    if (fileRef.current) fileRef.current.value = '';
    // Resume: a previous interrupted upload of THIS file continues from the missing chunk.
    setResumable(f.size > CHUNKED_THRESHOLD ? await getResumableUpload(f) : null);
  };

  const clearAudio = () => {
    if (audio?.url) URL.revokeObjectURL(audio.url);
    setAudio(null);
    setResumable(null);
  };

  const publish = async () => {
    if (!title.trim()) {
      toast({ title: 'Titolo mancante', description: 'Dai un titolo al tuo Racconto.', variant: 'destructive' });
      return;
    }
    if (!categorySlug) {
      toast({ title: 'Categoria mancante', description: 'Scegli una categoria per il tuo Racconto.', variant: 'destructive' });
      return;
    }
    if (!audio) {
      toast({ title: 'Audio mancante', description: 'Registra o carica un audio prima di pubblicare.', variant: 'destructive' });
      return;
    }
    if (isPaid && teaser.trim().length < MIN_TEASER) {
      toast({ title: 'Anteprima troppo corta', description: `L'anteprima pubblica richiede almeno ${MIN_TEASER} caratteri.`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const file = audio.file || new File([audio.blob], `racconto_${Date.now()}${extFromMime(audio.mime)}`, { type: audio.mime });
      const uploadFn = file.size > CHUNKED_THRESHOLD ? createPostChunked : createPost;
      await uploadFn({
        file,
        caption: desc.trim(),
        title: title.trim(),
        category_slug: categorySlug,
        duration_seconds: audio.duration || 0,
        is_paid: isPaid,
        price: isPaid ? parseInt(price, 10) : 0,
        preview_description: isPaid ? teaser.trim() : '',
        onProgress: (p) => setProgress(p),
      });
      toast({ title: 'Racconto pubblicato', description: 'Il tuo Racconto audio è ora nella tua Vetrina.' });
      clearAudio();
      navigate(`/u/${user.username}`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast({
        title: err.resumable ? 'Caricamento interrotto' : 'Pubblicazione non riuscita',
        description: err.resumable ? err.message : ((typeof detail === 'string' && detail.split('|').pop()) || err.message || 'Riprova.'),
        variant: 'destructive',
      });
      if (audio?.file) setResumable(await getResumableUpload(audio.file));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[720px] px-4 pt-6 pb-24" data-testid="racconto-audio-form">
      <button
        onClick={guardedBack}
        className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        data-testid="racconto-audio-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Indietro
      </button>
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="flex items-center gap-2 text-xl font-bold">
          <Mic className="h-5 w-5 gold-text" /> Racconto Audio
        </h2>

        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold">Titolo *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 150))}
              placeholder="Es. Una notte in Islanda"
              disabled={uploading}
              data-testid="racconto-audio-title"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Descrizione breve</label>
            <Textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value.slice(0, 1000))}
              placeholder="Di cosa parla questo Racconto? (facoltativo)"
              className="min-h-[70px] resize-none text-sm"
              disabled={uploading}
              data-testid="racconto-audio-desc"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Categoria *</label>
            <select
              value={categorySlug}
              onChange={(e) => setCategorySlug(e.target.value)}
              disabled={uploading}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="racconto-audio-category"
            >
              <option value="" disabled>Seleziona una categoria</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">Scegli tu la categoria: il tipo di contenuto non la determina.</p>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold">Audio *</label>
            {!audio && source === null && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={() => setSource('record')}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-4 text-sm font-bold gold-text hover:bg-amber-500/20"
                  data-testid="racconto-record-btn"
                >
                  <Mic className="h-4 w-4" /> Registra ora
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-4 text-sm font-bold hover:bg-secondary"
                  data-testid="racconto-upload-btn"
                >
                  <Upload className="h-4 w-4" /> Carica audio
                </button>
              </div>
            )}
            {!audio && (
              <p className="mt-2 text-[11px] text-muted-foreground" data-testid="racconto-audio-limit-hint">
                File audio: max 80 MB · circa 70 minuti (mp3). Se il caricamento si interrompe, riprende da dove si era fermato.
              </p>
            )}
            {!audio && source === 'record' && (
              <div className="space-y-2">
                <AudioRecorder onComplete={onRecorded} onRecordingChange={setRecording} />
                {!recording && (
                  <button
                    onClick={() => setSource(null)}
                    className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                    data-testid="racconto-source-back-btn"
                  >
                    Oppure carica un file audio
                  </button>
                )}
              </div>
            )}
            <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={onPickFile} data-testid="racconto-audio-file-input" />

            {audio && (
              <div className="rounded-xl border border-border bg-background p-4" data-testid="racconto-audio-preview">
                <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 font-semibold">
                    <Headphones className="h-3.5 w-3.5 gold-text" /> Anteprima
                  </span>
                  {audio.duration > 0 && <span className="tabular-nums" data-testid="racconto-audio-duration">{fmtDur(audio.duration)}</span>}
                </div>
                <audio src={audio.url} controls className="w-full" data-testid="racconto-audio-player" />
                {resumable && (
                  <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs gold-text" data-testid="racconto-resume-hint">
                    Caricamento precedente trovato: riprenderà dal {resumable.percent}% invece che da zero.
                  </p>
                )}
                <button
                  onClick={() => { const wasRec = audio.fromRecording; clearAudio(); setSource(wasRec ? 'record' : null); }}
                  disabled={uploading}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-50"
                  data-testid="racconto-audio-redo-btn"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> {audio.fromRecording ? 'Registra di nuovo' : 'Scegli un altro file'}
                </button>
              </div>
            )}
          </div>

          <MonetizationPicker
            scope="post_audio"
            isPaid={isPaid}
            setIsPaid={setIsPaid}
            price={price}
            setPrice={setPrice}
            teaser={teaser}
            setTeaser={setTeaser}
            disabled={uploading}
          />

          {uploading && (
            <div>
              <Progress value={progress} className="h-2" />
              <p className="mt-1 text-xs text-muted-foreground">Caricamento… {progress}%</p>
            </div>
          )}

          <button
            onClick={publish}
            disabled={uploading || !title.trim() || !audio || !categorySlug}
            className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient px-5 py-3 text-sm font-bold text-black disabled:opacity-50"
            data-testid="racconto-audio-publish-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {uploading ? 'Pubblicazione…' : (resumable ? `Riprendi dal ${resumable.percent}% e pubblica` : 'Pubblica Racconto')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaccontoAudioForm;
